/**
 * PJ モードを終えたときの行き先。
 *
 * **外した後も同じ対象を読み続けられなければ、外す操作は使われない。** PJ を外す口を
 * 「PJ 一覧へ戻る」に一本化すると、いま読んでいる業務・画面・フローのマスターを見るために
 * 一覧から辿り直すことになり、PJ を適用した姿とマスターを見比べる動きが毎回 2 手以上増える。
 * そこで行き先は現在地ごとに変える。
 *
 * | いまの現在地 | PJ を外した後の行き先 | なぜ |
 * |---|---|---|
 * | PJ を検索条件で受ける画面（`PJ_SEARCH_ROUTES` の全件） | 同じ画面から `?pj=` を落とす（節点 1 件を描く詳細で、その節点が案件の新設なら PJ サマリー） | 見ている対象は変わらない。新設された節点にマスターの姿は無い |
 * | PJ サマリー（`/pjs/$pjNumber`） | PJ 一覧 | PJ そのものが主語の画面なので、外すと対象が無くなる |
 * | PJ から見た業務フロー（`/pjs/$pjNumber/flows/$flowId`） | 同じ業務フローのマスター（案件が新設した業務フローなら PJ サマリー） | 同じフローの、PJ を当てていない姿。新設された業務フローにその姿は無い |
 *
 * **判断はここ 1 箇所に置く。** 画面の中に散らすと、ルートを足したときにどこを直せばよいかが
 * 分からなくなり、行き先を書き忘れた画面だけ外す口が壊れる。壊れたことは押すまで分からない。
 *
 * **新設かどうかはここでは決めない。** 決めるには案件の変更の行（取得）が要り、この関数は
 * ルートとパスパラメータだけで決まる純粋関数に保つ。ここが返すのは「どの節点を見ているか」
 * までで、新設なら案件サマリーへ倒す判断は描く側（`PjExitLink`）が変更の行から引く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ モードを終えたときの行き先を、現在地のルートから決める。
 * @graph-connects none
 */
import {
  BUSINESS_DETAIL_ROUTE,
  FLOW_PATH_PARAM,
  MASTER_DETAIL_ROUTE,
  PJ_DIFF_ROUTE,
  PJ_FLOW_ROUTE,
  PJ_PATH_PARAM,
  SCREEN_DETAIL_ROUTE,
  TERM_DETAIL_ROUTE,
  type PjModeRoute,
} from './route-paths.js';

/**
 * 節点 1 件を描く画面が、その節点の番号を受けるパスパラメータの名前。
 *
 * **一覧は載せない。** 一覧は節点を持たないので、外しても同じ一覧のマスターがいつも在る。
 * 載っていない現在地は「見ている節点が無い」として扱う。
 *
 * 名前はルートの雛形（`/terms/$termId` など）と同じでなければならない。ずれると節点が
 * 読めず、新設の節点でも「同じ画面から `?pj=` を落とす」へ倒れて外した先が「見つかりません」に
 * なる。雛形との突き合わせはテストが持つ。
 * @graph-connects none
 */
export const PJ_EXIT_NODE_PARAM: Readonly<Partial<Record<PjModeRoute, string>>> = {
  [BUSINESS_DETAIL_ROUTE]: 'businessId',
  [SCREEN_DETAIL_ROUTE]: 'screenId',
  [TERM_DETAIL_ROUTE]: 'termId',
  [MASTER_DETAIL_ROUTE]: 'id',
};

/**
 * PJ を外した後の行き先。
 *
 * 具体的なパスではなく行き先の種類で返すのは、ルーターのリンクが受ける形（相対の `.` /
 * 静的パス / パスパラメータ付き）が種類ごとに違うためである。パス文字列に畳むと、型付きの
 * リンクへ渡すときにアサーションが要る。
 */
export type PjExitTarget =
  /**
   * 同じ画面から検索条件だけを落とす。
   *
   * **節点 1 件を描く画面なら、その節点の番号（パスの綴り）を添える。** その節点が案件の新設
   * ならマスターに姿が無く、落とした先は「見つかりません」になる。描く側（`PjExitLink`）が
   * 案件の変更の行から新設かどうかを引き当て、新設なら案件サマリーへ倒すために要る。一覧なら
   * null。
   */
  | { readonly kind: 'dropSearch'; readonly nodeId: string | null }
  /** PJ 一覧へ移る。 */
  | { readonly kind: 'pjsList' }
  /**
   * 同じ業務フローのマスターの図へ移る。
   *
   * **その業務フローが案件の新設なら、マスターに姿が無い。** 出ていた案件の番号を添えるのは、
   * 描く側（`PjExitLink`）が案件の変更の行から新設かどうかを引き当て、新設なら案件サマリーへ
   * 倒すためである。番号を落とすと、描く側は「押したら見つかりません」に着くリンクしか作れない。
   */
  | { readonly kind: 'flow'; readonly flowId: string; readonly pjNumber: string };

/**
 * 現在地から、PJ を外した後の行き先を決める。
 *
 * 受け取るのは実 URL ではなくルートの定義パスである。実 URL で突き合わせるとパスパラメータの
 * 値ごとに判定が要り、ID に `/` を含むノード（`cons/styling-proposal` など）で必ず外れる。
 *
 * **業務フローが読めないときは PJ 一覧へ倒す。** PJ から見た業務フローは業務フローをパスに持つので実際には
 * 起こらないが、外す口は常に押せなければならない。押しても何も起きないリンクを出すくらいなら、
 * 確実に在る入口へ送る（ナビの `navLinkTarget` と同じ扱い）。
 *
 * @param route 現在地のルート定義パス
 * @param params 現在地のパスパラメータ
 * @returns PJ を外した後の行き先
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 現在地のルートとパスパラメータから、PJ を外した後の行き先を決める。
 * @graph-connects PJ_EXIT_NODE_PARAM [references] 節点 1 件を描く画面が節点の番号を受ける名前
 */
export function pjExitTarget(route: PjModeRoute, params: Record<string, unknown>): PjExitTarget {
  if (route === PJ_DIFF_ROUTE) return { kind: 'pjsList' };
  if (route !== PJ_FLOW_ROUTE) {
    const param = PJ_EXIT_NODE_PARAM[route];
    const nodeId = param === undefined ? undefined : params[param];
    return {
      kind: 'dropSearch',
      nodeId: typeof nodeId === 'string' && nodeId !== '' ? nodeId : null,
    };
  }

  const flowId = params[FLOW_PATH_PARAM];
  const pjNumber = params[PJ_PATH_PARAM];
  return typeof flowId === 'string' &&
    flowId !== '' &&
    typeof pjNumber === 'string' &&
    pjNumber !== ''
    ? { kind: 'flow', flowId, pjNumber }
    : { kind: 'pjsList' };
}
